// Периоды налогового календаря и оценки НДС: кварталы, месяцы, название обязательства
// по виду налога и периоду, подписи покрытия резерва. Чистые функции без импортов через
// `@…` — их проверяет `npm run test:financier` (taxPeriods.test.mjs рядом).

const ROMAN = ['I', 'II', 'III', 'IV'];

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

export interface Quarter {
  year: number;
  /** 1–4. */
  quarter: number;
}

/** Квартал даты «2026-11-03». */
export function quarterOf(iso: string): Quarter {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  return { year, quarter: Math.floor((month - 1) / 3) + 1 };
}

/** Код квартала для запроса: «2026-Q4» — его понимает сервер (`vat_quarter`). */
export function quarterKey(q: Quarter): string {
  return `${q.year}-Q${q.quarter}`;
}

/** Квартал из кода «2026-Q4»; не код квартала — `null`. */
export function quarterFromKey(key: string): Quarter | null {
  const m = /^(\d{4})-Q([1-4])$/.exec(key);
  return m ? { year: Number(m[1]), quarter: Number(m[2]) } : null;
}

/** Квартал словами: «IV кв. 2026» — так же его подписывает сервер. */
export function quarterLabel(q: Quarter): string {
  return `${ROMAN[q.quarter - 1]} кв. ${q.year}`;
}

export function previousQuarter(q: Quarter): Quarter {
  return q.quarter === 1 ? { year: q.year - 1, quarter: 4 } : { year: q.year, quarter: q.quarter - 1 };
}

/** Квартал даты и `count − 1` предыдущих — для выбора квартала на экране. */
export function recentQuarters(iso: string, count: number): Quarter[] {
  const out: Quarter[] = [];
  let q = quarterOf(iso);
  for (let i = 0; i < count; i += 1) {
    out.push(q);
    q = previousQuarter(q);
  }
  return out;
}

/** Месяц словами: «Ноябрь 2026» (дата «2026-11-03» или «2026-11»). */
export function monthLabel(iso: string): string {
  return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
}

/**
 * Сегодня по Москве (UTC+3, без перехода на летнее время). От этой даты считаются
 * текущие налоговый квартал и зарплатный месяц — как на сервере, при любом часовом поясе
 * компьютера. Местная дата пользователя (`todayIso`) — только для полей дат в формах.
 */
export function moscowTodayIso(now: Date = new Date()): string {
  return new Date(now.getTime() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}

/** Текущий квартал оценки НДС — по Москве, как его выбирает сервер. */
export function currentQuarterKey(now: Date = new Date()): string {
  return quarterKey(quarterOf(moscowTodayIso(now)));
}

/**
 * Показатели выбранного квартала: только по ответу именно на него (`currentData` запроса).
 * Пока квартал считается или если запрос не удался — загрузка или ошибка, а не цифры
 * прошлого квартала.
 */
export type QuarterKpiState<T> =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: T };

export function quarterKpiState<T>(query: {
  currentData: T | undefined;
  isError: boolean;
}): QuarterKpiState<T> {
  if (query.isError) return { kind: 'error' };
  if (query.currentData === undefined) return { kind: 'loading' };
  return { kind: 'ready', data: query.currentData };
}

/** Подсказки периода нового обязательства: прошлый и текущий месяц, прошлый и текущий квартал. */
export function taxPeriodSuggestions(todayIso: string): string[] {
  const year = Number(todayIso.slice(0, 4));
  const month = Number(todayIso.slice(5, 7));
  const prevMonth = month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
  const q = quarterOf(todayIso);
  return [
    monthLabel(prevMonth),
    monthLabel(todayIso),
    quarterLabel(previousQuarter(q)),
    quarterLabel(q),
  ];
}

const TITLE_BY_TYPE: Record<string, string> = {
  vat_1_3: '1/3 НДС за',
  ndfl: 'НДФЛ за',
  insurance_contrib: 'Страховые взносы за',
  profit_tax: 'Налог на прибыль за',
  property_tax: 'Налог на имущество за',
  ens_replenishment: 'Пополнение ЕНС за',
};

/**
 * Название обязательства из вида налога и периода: «1/3 НДС за III кв. 2026»,
 * «НДФЛ за ноябрь 2026». Нет вида или периода — пусто: название не придумываем.
 */
export function taxTitle(taxType: string, period: string): string {
  const prefix = TITLE_BY_TYPE[taxType];
  const p = period.trim();
  if (!prefix || !p) return '';
  const month = MONTHS.find((m) => p.startsWith(m));
  return `${prefix} ${month ? p.charAt(0).toLowerCase() + p.slice(1) : p}`;
}

/** Разница исходящего и входящего НДС (оценка), со знаком: меньше нуля — входящий больше. */
export function vatDifference(outgoing: number, incoming: number): number {
  return Math.round((outgoing - incoming) * 100) / 100;
}

/** Покрытие разницы остатком резерва. `null` — потребности нет, покрытие не требуется. */
export function coverageText(pct: number | null): string {
  return pct === null ? 'не требуется' : `${pct.toFixed(0)} %`;
}

/** Зарезервировано по календарю налогов: неуплаченные обязательства. */
export function calendarReserved(items: { status: string; reserved_amount: number }[]): number {
  const sum = items
    .filter((i) => i.status !== 'paid')
    .reduce((acc, i) => acc + (Number.isFinite(i.reserved_amount) ? i.reserved_amount : 0), 0);
  return Math.round(sum * 100) / 100;
}
